import { ModelRow } from "@mcleod/core";
import { Checkbox, DataSourceMode, Label, Table, TableRow, TableRowDisplayEvent } from "@mcleod/components";
import { EdiUtility } from "./EdiUtility";

export interface TableConfig {
    table: Table;
    label: Label;
    data: any[];
}

export class BaseCompare {
    protected _isCompareHistory: boolean = false;
    protected _isOrderUpdateAllowed: boolean = true;
    protected _isInvoice: boolean = false;

    // Constants for better maintainability
    protected static readonly COMPARE_ITEM_URL = "lme/datafusion/edi-compare-item";
    protected static readonly ALTERNATING_ROW_COLOR = "subtle.lightest";
    protected static readonly DEFAULT_ROW_COLOR = "#FFFFFF";

    // Getters and setters with more concise implementation
    get isOrderUpdateAllowed(): boolean { return this._isOrderUpdateAllowed; }
    set isOrderUpdateAllowed(value: boolean) { this._isOrderUpdateAllowed = value; }

    get isInvoice(): boolean { return this._isInvoice; }
    set isInvoice(value: boolean) { this._isInvoice = value; }

    get isCompareHistory(): boolean { return this._isCompareHistory; }
    set isCompareHistory(value: boolean) { this._isCompareHistory = value; }

    public setupTableData(tableConfigs: TableConfig[], mode: DataSourceMode): void {
        tableConfigs.forEach(config => this.configureTable(config, mode));
    }

    public configureTable(config: TableConfig, mode: DataSourceMode): void {
        const hasData = config.data && config.data.length > 0;
        
        config.label.visible = hasData;
        config.table.visible = hasData;
        
        if (hasData) {
            this.loadChildTableData(config.table, config.data, mode);
        }
    }

    public loadChildTableData(table: Table, data: any[], mode: DataSourceMode): void {
        const tableDataSource = table.dataSource;
        tableDataSource.data = [];
        
        const modelRows = data.map((element: any) => 
            new ModelRow(tableDataSource.url, true, element)
        );
        
        tableDataSource.setRowsAndMode(mode, modelRows);
    }

    public createCompareModelRows(compareItems: any[]): ModelRow[] {
        return compareItems.map(element => 
            new ModelRow(BaseCompare.COMPARE_ITEM_URL, true, element)
        );
    }

    public checkInvoiceOrderUpdateAllowed(rows: ModelRow[]): boolean {
        return this.isInvoice && rows.some(row => row?.getBoolean("allowed"));
    }

    public setupCheckboxBehavior(tableRow: TableRow, isUpdateAllowed: boolean, isAddDelete: boolean, isOtherChargeAddDelete: boolean = false): void {
        this.setupCheckboxBehaviorWithIds(tableRow, isUpdateAllowed, isAddDelete, "checkboxApply", "labelChecked", "labelApplyError", isOtherChargeAddDelete);
    }

    public setupCheckboxBehaviorWithIds(
        tableRow: TableRow, 
        isUpdateAllowed: boolean,
        isAddDelete: boolean, 
        checkboxId: string = "checkboxApply", 
        labelId: string = "labelChecked", 
        errorLabelId: string = "labelApplyError", 
        isOtherChargeAddDelete: boolean = false
    ): void {
        const checkboxApply = tableRow.findComponentById(checkboxId) as Checkbox;
        const labelChecked = tableRow.findComponentById(labelId) as Label;

        if (!this.isCompareHistory) {
            if (isUpdateAllowed) {
                checkboxApply.printable = false;
                if (isOtherChargeAddDelete) {
                    const modelRow: ModelRow = tableRow.data;
                    const compareItems: any[] = modelRow.get("compare_items") || [];
                    
                    if (compareItems.length > 0) {
                        const isApplied = compareItems.every(item => item["apply"] === true);
                        checkboxApply.checked = isApplied;
                        labelChecked.visible = isApplied;
                    }
                }
                checkboxApply.visible = true;
                checkboxApply.addClickListener(() => labelChecked.visible = checkboxApply.checked);
            } else {
                checkboxApply.visible = false;
            }
        } else {
            this.setupHistoryModeForRowWithIds(tableRow, checkboxApply, labelChecked, errorLabelId, isAddDelete);
        }
    }

    public setupHistoryMode(tableRow: TableRow, checkboxApply: Checkbox, labelChecked: Label): void {
        const labelApplyError = tableRow.findComponentById("labelApplyError") as Label;
        const modelRow: ModelRow = tableRow.data;
        
        checkboxApply.visible = false;
        labelApplyError.visible = true;
        labelChecked.visible = false;

        const compareItems: any[] = modelRow.get("compare_items") || [];
        const hasAppliedItems = compareItems.some(item => item["apply"] === true);
        
        labelApplyError.setProps(
            hasAppliedItems ? EdiUtility.getSuccessProps() : EdiUtility.getErrorProps()
        );
    }

    private setupHistoryModeForRowWithIds(
        tableRow: TableRow, 
        checkboxApply: Checkbox, 
        labelChecked: Label, 
        errorLabelId: string = "labelApplyError",
        isAddDelete: boolean
    ): void {
        const labelApplyError = tableRow.findComponentById(errorLabelId) as Label;
        const modelRow: ModelRow = tableRow.data;
        
        checkboxApply.visible = false;
        labelApplyError.visible = true;
        labelChecked.visible = false;
        let apply = false;
        if (isAddDelete) {
            const compareItems: any[] = modelRow.get("compare_items") || [];
            apply = compareItems.some(item => item["apply"] === true);
        } else {
            apply = modelRow.get("apply");  
        }
    
        labelApplyError.setProps(
            apply ? EdiUtility.getSuccessProps() : EdiUtility.getErrorProps()
        );
    }

    public applyAlternatingRowColor(target: any, index: number): void {
        target.backgroundColor = index % 2 === 0 
            ? BaseCompare.DEFAULT_ROW_COLOR 
            : BaseCompare.ALTERNATING_ROW_COLOR;
    }

    public processCompareItems(
        modelRowData: any[], 
        tableRow: TableRow, 
        compareItemClass: new() => any,
        changeItemClass: new() => any,
        setupCompareItem?: (compareItems: any) => void
    ): boolean {
        let isInvoiceOrderUpdateAllowed = false;
        
        tableRow.findComponentById((comp: any) => {
            if (comp instanceof compareItemClass || comp instanceof changeItemClass) {
                const compareItems = comp;
                const compareData = modelRowData[tableRow.index]["compare_items"] || [];
                const rows = this.createCompareModelRows(compareData);
                
                if (this.isInvoice && !isInvoiceOrderUpdateAllowed) {
                    isInvoiceOrderUpdateAllowed = this.checkInvoiceOrderUpdateAllowed(rows);
                }
                
                // Set isCompareHistory if the component supports it
                if ('isCompareHistory' in compareItems) {
                    compareItems.isCompareHistory = this.isCompareHistory;
                }
                
                // Set isOrderUpdateAllowed for change items
                if (comp instanceof changeItemClass && 'isOrderUpdateAllowed' in compareItems) {
                    compareItems.isOrderUpdateAllowed = 
                        (isInvoiceOrderUpdateAllowed && this.isInvoice) || this.isOrderUpdateAllowed;
                }
                
                // Allow for custom setup
                if (setupCompareItem) {
                    setupCompareItem(compareItems);
                }
                
                if ('setupTableData' in compareItems && typeof compareItems.setupTableData === 'function') {
                    compareItems.setupTableData(rows);
                }
            }
        });
        
        return isInvoiceOrderUpdateAllowed;
    }

    public handleAddDeleteRowDisplay(
        event: TableRowDisplayEvent, 
        dataArray: any[],
        compareItemClass: new() => any,
        changeItemClass: new() => any,
        isOtherChargeAddDelete: boolean = false
    ): void {
        const tableRow: TableRow = event.getTableRow();
        const isInvoiceOrderUpdateAllowed = this.processCompareItems(
            dataArray, 
            tableRow, 
            compareItemClass, 
            changeItemClass
        );
        
        const isUpdateAllowed = (this.isInvoice && isInvoiceOrderUpdateAllowed) || this.isOrderUpdateAllowed;
        this.setupCheckboxBehavior(tableRow, isUpdateAllowed, true, isOtherChargeAddDelete);
    }

    public handleChangeRowDisplay(
        event: TableRowDisplayEvent, 
        dataArray: any[], 
        compareItemClass: new() => any,
        changeItemClass: new() => any
    ): void {
        const tableRow: TableRow = event.getTableRow();
        this.processCompareItems(dataArray, tableRow, compareItemClass, changeItemClass);
        
        // Apply alternating row colors
        this.applyAlternatingRowColor(event.target, tableRow.index);
    }
}
